import { z } from 'zod';
import { brandChannelKindSchema as kindSchema } from './brand-channel-kind.js';
import { channelTakeoverProposalEnvelopeSchema } from './channel-takeover-proposal.js';
import { decisionTypedAuthorityMapSchema } from './trust-ledger.js';
// Brand channel — spec §0b. Always-on brand presence per channel kind.
// One row per (brand, kind). Owns connection state, per-channel
// authority overrides, operated_by_mag toggle, and pause window.
// Re-exported from the leaf module to break the circular import
// between this file and ./channel-takeover-proposal.ts. Consumers
// importing brandChannelKindSchema from '@magig/schemas' keep
// working unchanged.
export const brandChannelKindSchema = kindSchema;
export const brandChannelStatusSchema = z.enum(['active', 'pending_connection', 'archived']);
export const brandChannelSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    brandId: z.string().uuid(),
    kind: brandChannelKindSchema,
    status: brandChannelStatusSchema,
    connectedAt: z.string().datetime().nullable(),
    connectionRef: z.string().nullable(),
    /** Boolean on the wire; stored as text in the DB for forward-compat
     *  with a future tri-state. */
    operatedByMag: z.boolean(),
    pausedUntil: z.string().datetime().nullable(),
    authorityOverrides: decisionTypedAuthorityMapSchema.nullable(),
    createdAt: z.string().datetime(),
    /** [12.2L-177] Latest takeover-proposal envelope. Surfaces the
     *  proposed plan (build vs. continue, cadence, content pillars)
     *  on /home so the founder discovers mag's pitch without having
     *  to dig into the channel detail page. Null until the founder
     *  hits POST /channels/$kind/propose-takeover (auto-fired on Meta
     *  finalize). */
    takeoverProposal: channelTakeoverProposalEnvelopeSchema.nullable(),
    /** 47/N — autopilot on means mag drafts + renders + publishes
     *  without asking. Off means the JIT trigger lands a
     *  slot_approval_pending card on /home N hours before scheduled
     *  for each post. Default false for new channels; existing
     *  channels migrate to true (preserve current behavior). */
    autopilot: z.boolean().default(false),
    /** Hours before scheduled_for to fire the JIT prep + approval
     *  card. Default 36h covers one founder-tz evening; per-channel
     *  override via the settings page. Ignored when autopilot=true. */
    approvalLeadTimeHours: z.number().int().min(1).max(168).default(36),
    /** 50/N — channel-level creative mode default. 'mag' renders the
     *  asset via the LLM pipeline; 'designer' routes the brief to an
     *  external designer who uploads the rendered asset. Per-slot
     *  override available. */
    creativeMode: z.enum(['mag', 'designer']).default('mag'),
    /** 50b/N — designer's email address. Required when creativeMode
     *  is 'designer'. Used to send the brief + upload link on the
     *  JIT trigger. Null when designer mode is off.
     *
     *  [designer-role] When designerUserId is set (the founder picked a
     *  team member from the picker), this field carries that user's
     *  email so the existing slot-jit-prep + slot-designer-upload paths
     *  keep working unchanged. When designerUserId is null, this is
     *  the founder's manually-entered override (external designer
     *  without an account). */
    designerEmail: z.string().email().nullable().default(null),
    /** [designer-role] User id of the chosen team designer. Null when
     *  the route-to-designer toggle is off OR the founder is using a
     *  free-text email instead of picking a team member. The picker
     *  surface lists brand_members with role='designer'; this id is
     *  the picked member. */
    designerUserId: z.string().uuid().nullable().default(null),
});
/** Body the founder POSTs to connect / configure a channel. The
 *  status + connection_ref + connected_at fields are set server-side
 *  via the channel-specific oauth flow. */
export const upsertBrandChannelSchema = z.object({
    kind: brandChannelKindSchema,
    operatedByMag: z.boolean().optional(),
    pausedUntil: z.string().datetime().nullable().optional(),
    authorityOverrides: decisionTypedAuthorityMapSchema.nullable().optional(),
});
//# sourceMappingURL=brand-channel.js.map